/**
 * /admin/demandes-internes — Traitement des demandes envoyées par élèves/enseignants.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Filter, Send, CheckCircle2, Clock, AlertCircle, Paperclip } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const STATUS_FILTERS = [
  { value: '', label: 'Tous' },
  { value: 'new', label: 'Nouveaux' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'resolved', label: 'Résolus' },
];

export default function AdminInternalRequestsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('new');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.internalRequestListAdmin({ status: status || undefined, from_role: role || undefined });
      setItems(r?.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, [status, role]);

  useEffect(() => {
    document.title = 'Demandes internes · Admin';
    refresh();
  }, [refresh]);

  const submitReply = async (id) => {
    if (reply.trim().length < 1) { toast.error('Réponse vide'); return; }
    setReplying(true);
    try {
      await api.internalRequestReply(id, reply.trim(), 'resolved');
      toast.success('Réponse envoyée (email au demandeur)');
      setReply(''); setOpenId(null);
      refresh();
    } catch (e) { toast.error(e.message || 'Erreur'); }
    finally { setReplying(false); }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-internal-requests-page">
      <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">Administration · Demandes internes</div>
      <h1 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Demandes internes</h1>
      <p className="text-sm text-muted-foreground mt-2 mb-6">Questions, réclamations et suggestions envoyées par les élèves et enseignants.</p>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter size={13} className="text-muted-foreground" />
        <div className="flex gap-1" data-testid="filter-status">
          {STATUS_FILTERS.map(f => (
            <button key={f.value} onClick={() => setStatus(f.value)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${status === f.value ? 'bg-red-600 text-white' : 'bg-muted text-foreground/70 hover:bg-muted/70'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="h-4 w-px bg-border" />
        <div className="flex gap-1" data-testid="filter-role">
          {[{ v: '', l: 'Tous rôles' }, { v: 'eleve', l: 'Élèves' }, { v: 'enseignant', l: 'Enseignants' }].map(r => (
            <button key={r.v} onClick={() => setRole(r.v)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${role === r.v ? 'bg-red-600 text-white' : 'bg-muted text-foreground/70 hover:bg-muted/70'}`}>
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-muted-foreground text-sm">Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center">
          <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <div className="text-muted-foreground text-sm">Aucune demande pour ce filtre.</div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="admin-requests-list">
          {items.map(r => (
            <div key={r.id} className="rounded-xl border border-border bg-card" data-testid={`admin-req-${r.id}`}>
              <div className="p-4 flex items-start gap-3">
                <StatusIcon status={r.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-foreground font-semibold text-sm">{r.subject}</div>
                    <StatusBadge status={r.status} />
                    {r.has_attachment && <Paperclip size={11} className="text-muted-foreground" />}
                  </div>
                  <div className="text-muted-foreground text-xs mt-0.5">
                    <strong>{r.from_user_name}</strong> ({r.from_user_email}) · <span className="capitalize">{r.from_role}</span> · {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} · <em>{r.category.replace(/_/g, ' ')}</em>
                  </div>
                  <div className="text-foreground/80 text-sm mt-2 whitespace-pre-wrap">{r.message}</div>

                  {r.admin_reply && (
                    <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.05] p-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-1">Réponse admin · {r.admin_replied_by || ''}</div>
                      <div className="text-foreground/85 text-sm whitespace-pre-wrap">{r.admin_reply}</div>
                    </div>
                  )}

                  {openId === r.id ? (
                    <div className="mt-3 space-y-2" data-testid={`reply-form-${r.id}`}>
                      <textarea value={reply} onChange={e => setReply(e.target.value)} rows={4} placeholder="Ta réponse (envoyée par email au demandeur)…" className="w-full bg-background border border-input rounded-lg px-3 py-2 text-sm" />
                      <div className="flex items-center gap-2">
                        <button onClick={() => submitReply(r.id)} disabled={replying} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-black disabled:opacity-60" data-testid={`submit-reply-${r.id}`}>
                          <Send size={12} /> {replying ? 'Envoi…' : 'Envoyer et clore'}
                        </button>
                        <button onClick={() => { setOpenId(null); setReply(''); }} className="px-3 py-2 rounded-lg border border-input text-foreground/75 text-sm">Annuler</button>
                      </div>
                    </div>
                  ) : (
                    !r.admin_reply && (
                      <button onClick={() => { setOpenId(r.id); setReply(''); }} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 text-red-500 hover:bg-red-500/5 text-xs font-semibold" data-testid={`btn-reply-${r.id}`}>
                        <Send size={11} /> Répondre
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === 'resolved') return <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />;
  if (status === 'in_progress') return <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />;
  return <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />;
}

function StatusBadge({ status }) {
  const map = {
    new: { label: 'Nouveau', c: 'text-rose-600 bg-rose-500/15 border-rose-500/25 dark:text-rose-300' },
    in_progress: { label: 'En cours', c: 'text-amber-600 bg-amber-500/15 border-amber-500/25 dark:text-amber-300' },
    resolved: { label: 'Résolu', c: 'text-emerald-600 bg-emerald-500/15 border-emerald-500/25 dark:text-emerald-300' },
    archived: { label: 'Archivé', c: 'text-foreground/50 bg-muted border-border' },
  };
  const s = map[status] || map.new;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black uppercase tracking-widest ${s.c}`}>{s.label}</span>;
}
